import type { BlockContent } from '../grid/reduxStore/editorSlice.ts';
import { addGridBlockState, selectBlockContent, selectSectionById } from '../grid/reduxStore/editorSlice.ts';
import type { RootState } from '../grid/reduxStore/Store.ts';
import { setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice.ts';
import { deleteNewBlockWithSocket } from '../grid/reduxStore/gridBlockSocketActions.ts';
import type { BlockSettings } from '../grid/reduxStore/blockStyleSettingsSlice.ts';
import { addBlockSettings, deleteBlockSettings, selectBlockStyleById } from '../grid/reduxStore/blockStyleSettingsSlice.ts';
import { GridBlockType } from 'services/entities/BlockTypes';
import { AcknowledgmentResponseStatus } from '../../../services/socket/SocketEvents.tsx';
import { defaultBlockStyle } from '../hooks/useBlockSettings.ts';
import { addBlockViaSocket } from '../grid/reduxStore/saveHandlers.ts';
import type { BlockCommandDependencies } from './BlockBaseCommand.ts';
import { BlockBaseCommand } from './BlockBaseCommand.ts';
import type { SignatureBox } from 'services/entities/SignatureTypes';

export class DeleteBlockCommand extends BlockBaseCommand {
  private readonly originalBlockId: string;
  private currentBlockId: string;
  private readonly sectionId: string;
  private readonly blockSnapshot: BlockContent & { styles?: BlockSettings };
  private readonly getSignaturesBySectionId?: (sectionId: string) => SignatureBox[];

  constructor(
    blockId: string,
    sectionId: string,
    dependencies: BlockCommandDependencies & {
      getSignaturesBySectionId?: (sectionId: string) => SignatureBox[];
      currentState: RootState;
    }
  ) {
    super(dependencies);
    this.originalBlockId = blockId;
    this.currentBlockId = blockId;
    this.sectionId = sectionId;
    this.getSignaturesBySectionId = dependencies.getSignaturesBySectionId;

    const state = dependencies.currentState;
    const block = selectBlockContent(state, sectionId, blockId);
    const blockStyle = selectBlockStyleById(state, blockId) || null;
    const section = selectSectionById(state, sectionId);

    if (!block || !section) {
      throw new Error(`Block ${blockId} or section ${sectionId} not found`);
    }

    this.blockSnapshot = structuredClone({
      ...block,
      ...(blockStyle
        ? {
            styles: {
              ...defaultBlockStyle,
              ...blockStyle,
              blockId,
              updatedByUserId: dependencies.userId,
              currentTimestamp: new Date().toISOString(),
            },
          }
        : null),
    });
  }

  async undo(): Promise<boolean> {
    try {
      this.setSaving();

      const payload = this.buildBlockPayloadForSocket(this.blockSnapshot, this.originalBlockId, this.sectionId);

      const response = await addBlockViaSocket(this.socketClient, payload);

      const responseBlockId = response?.content.blockId;
      if (!responseBlockId) {
        this.setNotSaved();
        console.error('No blockId in response');
        return false;
      }
      this.currentBlockId = responseBlockId;

      const updatedContent = this.blockSnapshot.type === GridBlockType.TABLE ? this.blockSnapshot.contentTable : this.blockSnapshot.content;

      this.dispatch(
        addGridBlockState({
          sectionId: this.sectionId,
          blockId: this.currentBlockId,
          content: updatedContent,
          blockConfig: {
            ...this.blockSnapshot.blockConfig,
            id: this.currentBlockId,
          },
          blockType: this.blockSnapshot.type,
        })
      );

      if (this.blockSnapshot.styles) {
        this.dispatch(
          addBlockSettings({
            blockId: this.currentBlockId,
            blockSettings: {
              ...this.blockSnapshot.styles,
              blockId: this.currentBlockId,
            },
          })
        );
      }

      this.recalculatePageHeight(this.sectionId, this.getSignaturesBySectionId);

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: AcknowledgmentResponseStatus.OK,
        })
      );

      return true;
    } catch (error) {
      console.error('Failed to undo block deletion:', error);
      this.setNotSaved();
      return false;
    }
  }

  async redo(): Promise<boolean> {
    try {
      this.setSaving();
      this.dispatch(deleteBlockSettings({ blockId: this.currentBlockId }));

      const response = await this.dispatch(
        deleteNewBlockWithSocket({
          blockId: this.currentBlockId,
          socketClient: this.socketClient,
          userId: this.userId,
          callback: ({ status, errorCode }) => {
            this.handleDeleteSuccess(this.sectionId, this.currentBlockId, status, errorCode, this.getSignaturesBySectionId);
          },
        })
      ).unwrap();

      return response.status === AcknowledgmentResponseStatus.OK;
    } catch (error) {
      if (this.blockSnapshot.styles) {
        this.dispatch(
          addBlockSettings({
            blockId: this.currentBlockId,
            blockSettings: {
              ...this.blockSnapshot.styles,
              blockId: this.currentBlockId,
            },
          })
        );
      }
      this.setNotSaved();
      console.error('Failed to redo block deletion:', error);
      return false;
    }
  }

  getCurrentBlockId(): string {
    return this.currentBlockId;
  }
}
